import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { appendAvatarToFormData } from '../../../../utils/fileUpload'
import { showSuccessToast } from '@utils'
import { useLanguage } from '@context'

interface UpdateCategoryData {
  id: string
  name?: string
  contactAccountIds?: string[]
  avatar?: {
    uri: string
    type?: string
  }
}

const updateCategory = async (data: UpdateCategoryData): Promise<void> => {
  const { id, avatar, ...updateData } = data
  const formData = new FormData()
  
  if (updateData.name) {
    formData.append('name', updateData.name)
  }
  if (updateData.contactAccountIds !== undefined) {
    if (updateData.contactAccountIds.length > 0) {
      updateData.contactAccountIds.forEach((contactId) => {
        formData.append('contactAccountIds[]', contactId)
      })
    } else {
      // For empty arrays in FormData, send as JSON string
      // Backend needs to parse this - see update-category/http.request.ts
      formData.append('contactAccountIds', '[]')
    }
  }
  if (avatar) {
    appendAvatarToFormData(formData, avatar.uri, 'avatar')
  }

  await api.put(`/category/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export const useUpdateCategory = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<void, Error, UpdateCategoryData>({
    mutationFn: updateCategory,
    onSuccess: async (_, variables) => {
      showSuccessToast(t('toast.success'), t('toast.categoryUpdated'))
      // Invalidate both the list and the specific category
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['categories'] }),
        queryClient.invalidateQueries({ queryKey: ['category', variables.id] }),
        queryClient.invalidateQueries({ queryKey: ['category-contacts', { id: variables.id }] }),
      ])
    },
  })
}

